import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiContext, type ApiContextValue } from './ApiContext';
import { ApiHandler } from './handlers';
import type { IApiHandler } from './handlers/IApiHandler';
import { loadQueryDevtools } from '@/config/env';

/**
 * React Query devtools, in development builds only (BOS-236). A production
 * bundle carries no devtools code (`loadQueryDevtools` is `null` there).
 */
const lazyDevtools = (load: NonNullable<typeof loadQueryDevtools>) =>
    React.lazy(() =>
        load().then((module) => ({ default: module.ReactQueryDevtools })),
    );

const QueryDevtools = loadQueryDevtools
    ? lazyDevtools(loadQueryDevtools)
    : null;

type ApiProviderProps = {
    baseUrl: string;
    getAccessToken?: () => string | null;
    removeAccessToken?: () => void;
    /**
     * Supply the handler instead of letting the provider build one.
     *
     * This is how a test — or a local environment with no reachable ledger —
     * gets screens to resolve their data against something without making a
     * real request. See `ApiHandler`'s per-service options and
     * `tests/api/testApiProvider`.
     */
    apiClient?: IApiHandler;
    children: React.ReactNode;
};

export const ApiProvider: React.FC<ApiProviderProps> = ({
    baseUrl,
    getAccessToken,
    removeAccessToken,
    apiClient: injectedApiClient,
    children,
}) => {
    const [queryClient] = React.useState(() => new QueryClient());

    // `ApiHandler` reads `baseUrl` once at construction and threads it down to
    // every resource service. Re-create when `baseUrl` changes so a runtime
    // env swap (e.g. tests) doesn't keep the stale instance.
    const defaultApiClient = React.useMemo(
        () => new ApiHandler({ baseUrl }),
        [baseUrl],
    );
    const apiClient = injectedApiClient ?? defaultApiClient;

    const value = React.useMemo<ApiContextValue>(
        () => ({
            apiClient,
            baseUrl,
            getAccessToken,
            removeAccessToken,
        }),
        [apiClient, baseUrl, getAccessToken, removeAccessToken],
    );

    return (
        <QueryClientProvider client={queryClient}>
            <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
            {QueryDevtools ? (
                <React.Suspense fallback={null}>
                    <QueryDevtools buttonPosition="bottom-right" />
                </React.Suspense>
            ) : null}
        </QueryClientProvider>
    );
};
